import { User as UserIcon } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { IconActionButton } from '@/components/common/buttons/icon-action-button'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Can } from '@/features/sessao/providers/auth-context'
import UserSelector from '@/features/usuarios/components/user-selector'
import { Call, User } from '@/services/backend/config/entities'

import { ChamadoService } from '../../services/chamado.service'

type Props = {
  callId: number
  callStatus: string
  onSuccess: (data: Call) => void
}

function useFormHook({ callId, onSuccess }: Props) {
  const [user, setUser] = useState<Omit<User, 'password'>>()

  const form = useForm<Parameters<typeof ChamadoService.techAssign>[0]>({
    defaultValues: { id: callId },
  })

  const onSubmit = form.handleSubmit(async (data) => {
    if (!user) return toast.error('Selecione um usuário')

    try {
      const res = await ChamadoService.techAssign({
        ...data,
        techUserId: user.id,
      })
      onSuccess(res)
    } catch {
      // Error toast is already shown by the API connector interceptor
    }
  })

  return { onSubmit, user, setUser }
}

function CreateForm(props: Props) {
  const { onSubmit, user, setUser } = useFormHook(props)

  return (
    <form onSubmit={onSubmit}>
      <div className="flex flex-col gap-4">
        <div>
          <Label className="mb-0 text-sm">Usuário</Label>
          <div className="flex items-center gap-2">
            <Input value={user?.displayName ?? ''} disabled />
            <UserSelector onSelect={setUser} />
          </div>
        </div>

        <div className="mt-8 flex justify-between">
          <Button type="submit">Enviar</Button>
        </div>
      </div>
    </form>
  )
}

export default function Component({ onSuccess, ...props }: Props) {
  const [isOpen, setIsOpen] = useState(false)

  const isEditable = ['OPEN', 'ASSIGNED'].includes(props.callStatus)
  if (!isEditable) return null

  return (
    <Can asDefault I="update" a="Call" field="techUserId">
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <IconActionButton icon={UserIcon} tooltip="Técnico" />
        </DialogTrigger>

        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atribuir técnico</DialogTitle>
          </DialogHeader>

          <CreateForm
            {...props}
            onSuccess={(res) => {
              setIsOpen(false)
              onSuccess(res)
            }}
          />
        </DialogContent>
      </Dialog>
    </Can>
  )
}
